import supabase from '../supabaseClient';
import { getLocalDateString, normalizeDateString } from './dateUtils';
import { LATE_CANCELLATION_THERAPIST_FEE } from './graceCancellationUtils';
import {
  computePayrollIePenaltyInPeriod,
  IE_PENALTY_PAYROLL_EFFECTIVE_DATE,
} from './initialEvaluationUtils';
import { SERVICE_TYPE_LABELS } from './serviceTypeUtils';

export {
  PROGRESS_REPORT_FEES,
  PROGRESS_REPORT_LEAD_DAYS,
} from './progressReportUtils';
export {
  GRACE_CANCELLATIONS_PER_YEAR,
  LATE_CANCELLATION_THERAPIST_FEE,
} from './graceCancellationUtils';

export const MENTORSHIP_HOURLY_RATE = 950;

export const EXTRA_TYPES = {
  PROGRESS_REPORT: 'progress_report',
  MENTORSHIP: 'mentorship',
  LATE_CANCELLATION: 'late_cancellation',
  INITIAL_EVALUATION_PENALTY: 'initial_evaluation_penalty',
};

export function buildIePenaltyExtras(ieRows, startDate, endDate) {
  if (!Array.isArray(ieRows) || normalizeDateString(endDate) < IE_PENALTY_PAYROLL_EFFECTIVE_DATE) {
    return [];
  }

  const today = getLocalDateString(new Date());
  const extras = [];

  ieRows.forEach((row) => {
    const submittedDate = normalizeDateString(row.submitted_at);
    let asOfDate;

    if (row.status === 'submitted') {
      if (!submittedDate || submittedDate < IE_PENALTY_PAYROLL_EFFECTIVE_DATE) {
        return;
      }
      asOfDate = submittedDate;
    } else {
      asOfDate = today < endDate ? today : endDate;
    }

    const penaltyAmount = computePayrollIePenaltyInPeriod(
      row.due_date,
      startDate,
      endDate,
      asOfDate
    );

    if (penaltyAmount <= 0) {
      return;
    }

    extras.push({
      id: `ie-penalty-${row.id}-${startDate}`,
      type: EXTRA_TYPES.INITIAL_EVALUATION_PENALTY,
      date: endDate,
      label: `IE Late Penalty — ${row.client?.name || 'Client'}`,
      amount: -penaltyAmount,
      isWaived: false,
    });
  });

  return extras;
}

/**
 * Fetch payroll extras for a therapist within a date range (inclusive).
 * Extras are not subject to withholding tax.
 */
export async function fetchTherapistExtras(therapistId, startDate, endDate) {
  const extras = [];

  const [
    progressReportsResult,
    mentorshipResult,
    lateCancellationsResult,
    iePenaltiesResult,
  ] = await Promise.all([
      supabase
        .from('progress_report_requests')
        .select(`
          id,
          submitted_at,
          therapist_fee,
          service_type,
          client:clients (name)
        `)
        .eq('therapist_id', therapistId)
        .eq('status', 'submitted')
        .not('submitted_at', 'is', null)
        .gte('submitted_at', `${startDate}T00:00:00`)
        .lte('submitted_at', `${endDate}T23:59:59`),
      supabase
        .from('mentorship_sessions')
        .select(`
          id,
          session_date,
          session_time,
          hours,
          therapist_amount,
          mentee:mentee_therapist_id (name)
        `)
        .eq('mentor_therapist_id', therapistId)
        .gte('session_date', startDate)
        .lte('session_date', endDate),
      supabase
        .from('session_attendance')
        .select(`
          id,
          attendance_date,
          grace_cancellation,
          waived_late_cancellation,
          client:clients (name)
        `)
        .eq('therapist_id', therapistId)
        .eq('status', 'absent')
        .gte('attendance_date', startDate)
        .lte('attendance_date', endDate),
      supabase
        .from('initial_evaluation_submissions')
        .select(`
          id,
          due_date,
          status,
          submitted_at,
          client:clients (name)
        `)
        .eq('therapist_id', therapistId),
    ]);

  if (progressReportsResult.error) {
    throw progressReportsResult.error;
  }
  if (mentorshipResult.error) {
    throw mentorshipResult.error;
  }
  if (lateCancellationsResult.error) {
    throw lateCancellationsResult.error;
  }
  if (iePenaltiesResult.error) {
    throw iePenaltiesResult.error;
  }

  progressReportsResult.data?.forEach((request) => {
    const serviceLabel =
      SERVICE_TYPE_LABELS[request.service_type] || request.service_type;
    extras.push({
      id: `pr-${request.id}`,
      type: EXTRA_TYPES.PROGRESS_REPORT,
      date: request.submitted_at?.split('T')[0],
      label: `Progress Report — ${request.client?.name || 'Client'} (${serviceLabel})`,
      amount: Number(request.therapist_fee) || 0,
      isWaived: false,
    });
  });

  mentorshipResult.data?.forEach((session) => {
    const hours = Number(session.hours) || 0;
    extras.push({
      id: `ms-${session.id}`,
      type: EXTRA_TYPES.MENTORSHIP,
      date: session.session_date,
      label: `Mentorship — ${session.mentee?.name || 'Mentee'} (${hours} hr${hours === 1 ? '' : 's'})`,
      amount: Number(session.therapist_amount) || 0,
      isWaived: false,
    });
  });

  extras.push(...buildIePenaltyExtras(iePenaltiesResult.data, startDate, endDate));

  lateCancellationsResult.data?.forEach((attendance) => {
    const isGrace = Boolean(attendance.grace_cancellation);
    const isWaivedLate = Boolean(attendance.waived_late_cancellation);
    const feeWaived = isGrace || isWaivedLate;
    extras.push({
      id: `lc-${attendance.id}`,
      type: EXTRA_TYPES.LATE_CANCELLATION,
      date: attendance.attendance_date,
      label: isGrace
        ? `Grace Cancellation — ${attendance.client?.name || 'Client'}`
        : isWaivedLate
          ? `Waived Late Cancellation — ${attendance.client?.name || 'Client'}`
          : `Late Cancellation — ${attendance.client?.name || 'Client'}`,
      amount: feeWaived ? 0 : LATE_CANCELLATION_THERAPIST_FEE,
      isWaived: feeWaived,
    });
  });

  extras.sort((a, b) => {
    const dateCompare = (a.date || '').localeCompare(b.date || '');
    if (dateCompare !== 0) {
      return dateCompare;
    }
    return a.label.localeCompare(b.label);
  });

  return extras;
}

export function computeExtrasTotal(extras) {
  if (!Array.isArray(extras)) {
    return 0;
  }

  return extras.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
}

export function formatExtraAmount(item, formatCurrency) {
  if (item.isWaived) {
    return 'Waived';
  }
  return formatCurrency(item.amount);
}
